<template>
  <v-card class="w-100 d-flex flex-column ga-4 pa-8">
    <v-sheet class="header-section">
      <h3 class="title-section">
        Probabilidade de conversões por etapa e processo 
        <v-tooltip location="top">
          <template #activator="{ props }">
            <label class="empty-data" v-if="!internalData.success" v-bind="props">
              Dados insuficientes
              <v-icon size="14" class="summary-info" color="grey">
                mdi-help-circle
              </v-icon>
            </label>
          </template>

          Filtro não retornou dados.
        </v-tooltip>
      </h3>
      <p class="subtitle-section">
        Média geral da probabilidade de conversão por etapa e processo atribuída aos registros na última atualização.
      </p>
    </v-sheet>

    <rb-loader v-if="isLoading" v-model="internalData" :contained="true" />

    <FilterComponent
      v-model:view="mode"
      v-model:search="search"
      :available-ids="availableIdsByCategory"
      :show-chart="true"
      :show-table="true"
      :show-search="true"
      :show-filter="false"
      :show-refresh="false"
      :show-process-segmentation="true"
      :filters="filters"
      :show-download="mode === 'table' && rows && rows.length > 0"
      :download-disabled="!rows || rows.length === 0"
      @click:download="openDownloadCsvPopup"
      @click:chart="mode = 'chart'"
      @click:table="mode = 'table'"
      @click:filter="abrirDrawerFiltros()"
      @submit:search="handleSearchSubmit"
      @update:segmentation="onSegmentationChange"
      @update:selectedProcess="onProcessChange"
    />
    <v-sheet v-if="mode === 'chart'" class="w-100 d-flex">
      <VerticalGroupedBarScroll
        v-if="segmentationMode !== 'process' || !selectedProcess"
        :rows="chartRows"
        :height="500"
        :visible-count="8"
        :color-range="['#EDF5F1', '#0EE378', '#000D1C']"
        :wrap-label="wrapProcessLabel"
        @bar-click="handleBarClick"
      />

      <div
        v-else
        ref="barRef"
        style="flex:1 1 100%; height:500px; position:relative; pointer-events:auto;"
      ></div>
    </v-sheet>
    <v-sheet v-if="mode === 'table'" class="w-100 d-flex">
      <TableInsights
        :headers="tableHeaders"
        :items="rows"
        :show-total="true"
        total-title="Total geral/Média geral"
        :total-col-span="2"
        :totals="tableTotals"
        v-model:page="page"
        v-model:itemsPerPage="ipp"
        :items-per-page-options="[5,10,25,50]"
        :highlight-index="1"
        @row:click="handleRowClick($event)"
      >
        <template #tooltip-content>
          Exibe a média da <strong>probabilidade de conversão</strong> segmentada por etapa do funil e processo, demonstrando a evolução do potencial de fechamento ao longo da jornada.
        </template>
        <template #cell:lt60="{ value }">
          <div class="d-flex align-center justify-end ga-1">
            <span>{{ value }}</span>
            <v-icon
              v-if="pctToNumber(value) >= 15 && pctToNumber(value) < 20"
              size="16"
              color="#FF8A00"
            >mdi-alert-circle</v-icon>
          </div>
        </template>

        <template #total:lt60="{ value }">{{ value }}</template>
        <template #total:between60_70="{ value }">{{ value }}</template>
        <template #total:between70_90="{ value }">{{ value }}</template>
        <template #total:gt90="{ value }">{{ value }}</template>
      </TableInsights>
    </v-sheet>

    <DetailPopUp
      v-model="detailPopUpVisible"
      :payload="payloadDetails"
      :url="urlDetailPopUp"
      :title-pop-up="detailPopUpTitle"
      subtitle-pop-up="Encontre o contato para realizar análises qualitativas"
      :headers="[
            { title:'Nome', key:'nome', align:'start' },
            { title:'Probabilidade', key:'probabilidade' },
            { title: 'Processo', key: 'processo'},
            { title:'Etapa', key:'etapa' },
          ]"
      :rows-keys="['id', 'nome', 'probabilidade', 'processo', 'etapa']"
      @after-leave="detailPopUpVisible=false"
    />

    <RbPopUp
      v-model="downloadCsvPopupVisible"
      :width="400"
      confirm-text="Download"
      cancel-text="Cancelar"
      confirm-color="primary-60"
      variant-confirm="elevated"
      variant-cancel="outlined"
      :disabled-confirm="!rows || !rows.length"
      @confirm="downloadTableCsv"
    >
      <template #title>Download do relatório (CSV)</template>

      <template #message>
        Realize o download em CSV do widget<br />
        "<strong>Probabilidade de conversões por etapa e processo</strong>".
      </template>

      <template #confirm-icon>
        <v-icon size="18">mdi-download</v-icon>
      </template>
    </RbPopUp>
  </v-card>
</template>

<script>
import RbLoader from '@/ai_studio/components/common/RbLoader.vue';
import FilterComponent from './FilterComponent.vue';
import TableInsights from './TableInsights.vue';
import DetailPopUp from './detailsPopUp/DetailPopUp.vue';
import VerticalGroupedBarScroll from './charts/VerticalGroupedBarScroll.vue'
import * as echarts from 'echarts';
import { PostWithJson } from '@/ai_studio/http/http';
import { downloadCsv } from '@/ai_studio/utils/exportCsv';
import { calculateAvailableIds } from '@/ai_studio/utils/entityHelper';
import RbPopUp from '@/ai_studio/components/common/RBPopup.vue';

export default {
  name: 'ConversionByStepProcess',

  components: {
    RbLoader,
    FilterComponent,
    TableInsights,
    DetailPopUp,
    VerticalGroupedBarScroll,
    RbPopUp
  },

  props: {
    data: { type: Array, default: () => [] },
    filters: { type: Array, default: () => [] },
  },

  data() {
    return {
      mode: 'chart',
      barChart: null,
      resizeHandler: null,
      page: 1,
      ipp: 5,
      search: '',
      internalData: this.data,
      originalAvailableIds: null,
      segmentationMode: 'general',
      selectedProcess: null,
      isLoading: false,
      detailPopUpTitle: '',
      urlDetailPopUp: '',
      titleEtapa: '',
      idProcesso: null,
      payloadDetails: {},
      detailPopUpVisible: false,
      downloadCsvPopupVisible: false,
    }
  },
  
  watch: {
    data: {
      deep: true,
      immediate: true,
      handler(newVal) {
        this.internalData = newVal;
        if (newVal && !this.originalAvailableIds) {
          const ids = calculateAvailableIds(newVal);
          if (Object.keys(ids).length > 0) {
            this.originalAvailableIds = ids;
          }
        }
      }
    },
    internalData: {
      deep: true,
      handler() {
        const shouldShowFunnel = (this.mode === 'chart' && this.segmentationMode === 'process' && this.selectedProcess)
        if (shouldShowFunnel) {
          this.disposeCharts()
          this.$nextTick(() => this.initCharts())
        }
      }
    },
    mode: {
      immediate: true,
      handler(newVal, oldVal) {
        const shouldShowFunnel = (newVal === 'chart' && this.segmentationMode === 'process' && this.selectedProcess)

        if (shouldShowFunnel) {
          this.$nextTick(() => this.initCharts())
        } else if (oldVal === 'chart') {
          this.disposeCharts()
        }
      }
    },
    segmentationMode() {
      if (this.mode === 'chart') {
        this.disposeCharts()
        this.$nextTick(() => this.initCharts())
      }
    },
    selectedProcess: {
      deep: true,
      handler() {
        if (this.mode === 'chart' && this.segmentationMode === 'process') {
          this.disposeCharts()
          this.$nextTick(() => this.initCharts())
        } else {
          this.disposeCharts()
        }
      }
    }
  },

  beforeUnmount() {
    this.disposeCharts()
  },

  computed: {
    sourceData() {
      return Array.isArray(this.internalData?.dados_step)
        ? this.internalData.dados_step
        : Array.isArray(this.internalData)
          ? this.internalData
          : []
    },

    filteredData() {
      const base = this.sourceData
      if ( this.segmentationMode !== 'process' || !this.selectedProcess) {
        return base
      }

      const selectedName = 
        this.selectedProcess.nome ||
        this.selectedProcess.name ||
        this.selectedProcess.processo ||
        this.selectedProcess.title
      
      if ( !selectedName ) return base

      return base.filter(item => (item.processo || item.processo_nome || item.nome) === selectedName)
    },

    rows() {
      return this.filteredData.map(item => ({
        step: item.etapa,
        processo: item.processo,
        idProcesso: item.idProcesso,
        inscricoes: Number(item.inscricoes) || 0,
        probabilidade: `${(Number(item.media) || 0).toFixed(2)}%`,
      }))
    },
    chartRows() {
      return this.filteredData.map(item => ({
        processo: item.processo ?? item.processo_nome ?? item.nome ?? 'N/A',
        idProcesso: item.idProcesso ?? null,
        etapa: item.etapa ?? 'N/A',
        inscricoes: Number(item.inscricoes) || 0,
        media: Number(item.media) || 0,
        meta: item.meta ?? null
      }))
    },

    tableTotals() {
      const stats = this.filteredData.reduce((acc, item) => {
        const inscricoes = Number(item.inscricoes) || 0
        const media = Number(item.media) || 0

        acc.totalInscricoes += inscricoes
        acc.weightedMediaSum += (media * inscricoes)
        
        return acc
      }, {
        totalInscricoes: 0,
        weightedMediaSum: 0
      })

      const mediaGeral = (stats.totalInscricoes > 0)
        ? (stats.weightedMediaSum / stats.totalInscricoes)
        : 0
      return {
        inscricoes: stats.totalInscricoes,
        probabilidade: `${mediaGeral.toFixed(2)}%`
      }
    },
    showNoDataMessage() {
      return (
        !this.isLoading &&
        this.segmentationMode === 'process' &&
        this.selectedProcess &&
        this.sourceData.length > 0 &&
        this.filteredData.length === 0
      )
    },
    tableHeaders() {
      return [
        { title:'Etapa', key:'step' },
        { title:'Processo', key:'processo' },
        { title:'Inscrições', key:'inscricoes' },
        { title:'Probabilidade', key:'probabilidade' },
      ]
    },
    availableIdsByCategory() {
      if (this.originalAvailableIds) return this.originalAvailableIds;
      return calculateAvailableIds(this.sourceData);
    },
  },
  
  methods: {
    openDownloadCsvPopup() {
      if (!this.rows || !this.rows.length) return
      this.downloadCsvPopupVisible = true
    },

    downloadTableCsv() {
      const fields = this.tableHeaders.map(header => header.title)

      const data = (this.rows || []).map(row => ([
        row.step ?? '',
        row.processo ?? '',
        Number(row.inscricoes || 0).toLocaleString('pt-BR'),
        String(row.probabilidade ?? '')
      ]))

      if (this.tableTotals) {
        const totalInscricoes = Number(this.tableTotals.inscricoes || 0).toLocaleString('pt-BR')
        const totalProbabilidades = String(this.tableTotals.probabilidade ?? '')

        data.push([
          'Resumo dos dados',
          '',
          totalInscricoes,
          totalProbabilidades
        ])
      }

      const today = new Date().toISOString().slice(0, 10)
      const segmentation =
        (this.segmentationMode === 'process' && this.selectedProcess)
          ? '_por_processo'
          : '_geral'

      downloadCsv({
        filename: `probabilidade_conversoes_por_etapa_e_processo_${today}${segmentation}.csv`,
        fields,
        data,
        delimiter: ';'
      })
    },

    abrirDrawerFiltros() {},

    wrapProcessLabel(value) {
      if (!value) return ''
      const v = String(value)
      if (v.length <= 18) return v
      return (
        v.slice(0, 18) +
        '\n' +
        v.slice(18, 36) +
        (v.length > 36 ? '…' : '')
      )
    },

    handleBarClick(d) {
      this.handleRowClick({
        step: d.etapa,
        processo: d.processo,
        idProcesso: d.idProcesso
      })
    },

    handleRowClick(row) {
      this.detailPopUpTitle = `${row.step} - ${row.processo}`
      this.detailPopUpVisible = true
      this.urlDetailPopUp = 'insights/getReportForecastByStepProcess'
      this.payloadDetails = {
        process: row.idProcesso,
        step: row.step
      }
    },
    
    onSegmentationChange(mode) {
      if(mode == 'general') this.selectedProcess = null
      this.segmentationMode = mode
    },

    onProcessChange(process) {
      this.selectedProcess = process
    },

    pctToNumber(value) {
      if (typeof value === 'string') {
        return parseFloat(value.replace('%', '')) || 0
      }
      return Number(value) || 0
    },

    disposeCharts() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
      if (this.barChart) {
        this.barChart.dispose()
        this.barChart = null
      }
    },
    
    buildSeriesFromData() {
      const items = this.filteredData;
      if (!items || items.length === 0) {
        return {
          allProcessos: [],
          allEtapas: [],
          dataset: [],
          series: [],
          maxInscricoesGlobal: 0
        };
      }

      const allProcessosSet = new Set(items.map(item => item.processo || 'N/A'));
      const allEtapasSet = new Set(items.map(item => item.etapa || 'N/A'));

      const allProcessos = [...allProcessosSet].sort();
      const allEtapas = [...allEtapasSet].sort();

      const dataMap = new Map();
      items.forEach(item => {
        const processo = item.processo || 'N/A';
        const etapa = item.etapa || 'N/A';
        const key = `${processo}|${etapa}`;
        dataMap.set(key, {
          inscricoes: Number(item.inscricoes) || 0,
          media: Number(item.media) || 0
        });
      });

      const rows = [];
      let maxInscricoesGlobal = 0;

      for (const processo of allProcessos) {
        for (const etapa of allEtapas) {
          const key = `${processo}|${etapa}`;
          const item = dataMap.get(key) || { inscricoes: 0, media: 0 };

          const inscricoes = Number(item.inscricoes) || 0;
          const mediaRaw = Number(item.media) || 0;
          const media = Math.max(0, Math.min(100, mediaRaw));

          rows.push({
            processo,
            etapa,
            inscricoes,
            media
          });

          if (inscricoes > maxInscricoesGlobal) {
            maxInscricoesGlobal = inscricoes;
          }
        }
      }

      const dataset = [
        {
          id: 'raw',
          source: rows
        },
        ...allEtapas.map(etapa => ({
          id: `ds-${etapa}`,
          fromDatasetId: 'raw',
          transform: {
            type: 'filter',
            config: {
              dimension: 'etapa',
              '=': etapa
            }
          }
        }))
      ];

      const series = allEtapas.map((etapa, idx) => ({
        name: etapa,
        type: 'bar',
        datasetIndex: idx + 1
      }));

      return {
        allProcessos,
        allEtapas,
        dataset,
        series,
        maxInscricoesGlobal
      };
    },

    async initCharts() {
      if (this.mode !== 'chart') return

      const shouldShowFunnel = (this.segmentationMode === 'process' && this.selectedProcess)
      if (!shouldShowFunnel) {
        this.disposeCharts()
        return
      }

      const items = this.filteredData
      this.initFunnelChart(items)

      if (!this.resizeHandler) {
        this.resizeHandler = () => {
          if (this.barChart) this.barChart.resize()
        }
        window.addEventListener('resize', this.resizeHandler)
      }
    },

    initVerticalBar(allProcessos, allEtapas, series, dataset, maxInscricoesGlobal) {
      const el = this.$refs.barRef;
      this.barChart = echarts.init(el);

      const { max: dynamicMax, interval: dynamicInterval } =
        this.calculateAxisParams(maxInscricoesGlobal);

      const colorRange = ['#EDF5F1', '#0EE378', '#000D1C'];

      const barOption = {
        title: { show: false },

        dataset,

        tooltip: {
          trigger: 'item',
          axisPointer: { type: 'shadow' },
          confine: true,
          backgroundColor: '#fff',
          borderColor: '#C5C7C6',
          borderWidth: 1,
          padding: [8, 10],
          textStyle: { color: '#191C1C', fontSize: 12 },
          formatter: (params) => {
            const arr = Array.isArray(params) ? params : [params];
            if (!arr.length) return '';

            const sorted = arr.slice().sort(
              (a, b) =>
                Number(b.data?.inscricoes ?? 0) -
                Number(a.data?.inscricoes ?? 0)
            );

            let tip = '';
            sorted.forEach(p => {
              const processo = p.data?.processo || p.name || '';
              const etapa = p.data?.etapa || p.seriesName || '';
              const qtd = Number(p.data?.inscricoes ?? 0);
              const media = Number(p.data?.media ?? 0);
              tip += `<strong>${processo}</strong><br/>`;
              tip += `${p.marker} <strong>Etapa:</strong> ${etapa}<br/>`;
              tip += `&nbsp;&nbsp;Inscrições: ${qtd.toLocaleString('pt-BR')}`;
              tip += ` — Média: ${media.toFixed(2)}%<br/>`;
            });
            return tip;
          }
        },

        legend: { show: false },

        grid: {
          left: 12,
          right: 80,
          top: 10,
          bottom: 110,
          containLabel: true
        },

        xAxis: {
          type: 'category',
          axisTick: { show: false, alignWithLabel: true },
          axisLine: { lineStyle: { color: '#C5C7C6' } },
          axisLabel: {
            interval: 0,
            margin: 12,
            fontSize: 11,
            formatter: (value) => {
              if (!value) return '';
              const v = String(value);
              if (v.length <= 18) return v;
              return (
                v.slice(0, 18) +
                '\n' +
                v.slice(18, 36) +
                (v.length > 36 ? '…' : '')
              );
            }
          }
        },

        yAxis: {
          type: 'value',
          min: 0,
          max: dynamicMax,
          interval: dynamicInterval,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            formatter: (v) => Number(v).toLocaleString('pt-BR'),
            fontSize: 11
          },
          splitLine: { show: true, lineStyle: { color: '#EAEBEA' } }
        },

        visualMap: {
          type: 'continuous',
          orient: 'vertical',
          right: 10,
          top: 'center',
          min: 0,
          max: 100,
          dimension: 'media',
          formatter: v => `${Math.round(v)}%`,
          text: ['100%', '0%'],
          textStyle: { color: '#191C1C', fontWeight: 'bold' },
          inRange: { color: colorRange },
          calculable: false,
          itemHeight: 180,
          itemWidth: 12
        },


        series: series.map(s => ({
          ...s,
          barMaxWidth: 14,
          barGap: '12%',
          barCategoryGap: '20%',
          encode: {
            x: 'processo',
            y: 'inscricoes',
            tooltip: ['processo', 'etapa', 'inscricoes', 'media'],
            itemName: 'processo'
          },
          emphasis: { focus: 'series' },
          animation: false
        }))
      };

      this.barChart.setOption(barOption);
    },

    initFunnelChart(items) {
      const el = this.$refs.barRef;
      this.barChart = echarts.init(el);

      const colorRange = ['#EDF5F1', '#0EE378', '#000D1C'];

      const data = items
        .map(item => ({
          name: item.etapa || 'N/A',
          value: Number(item.inscricoes) || 0,
          media: Number(item.media) || 0
        }))
        .sort((a, b) => b.value - a.value);

      const option = {
        tooltip: {
          trigger: 'item',
          axisPointer: { type: 'shadow'},
          confine: true,
          backgroundColor: '#fff',
          borderColor: '#C5C7C6',
          borderWidth: 1,
          padding: [8, 10],
          textStyle: { color: '#191C1C', fontSize: 12 },
          formatter: (params) => {
            let arr = Array.isArray(params) ? params : [params];
            if (!arr.length) return '';
            let sorted = arr.slice().sort(
              (a, b) =>
                Number(b.data?.value ?? 0) -
                Number(a.data?.value ?? 0)
            );
            let tip = ''
            sorted.forEach(p => {
              let etapa = p.data?.name;
              let qtd = Number(p.data?.value ?? 0)
              let media = Number(p.data?.media ?? 0)

              tip += `${p.marker} <strong>Etapa:</strong> ${etapa}<br/>`;
              tip += `&nbsp;&nbsp;Inscrições: ${qtd.toLocaleString('pt-br')}`;
              tip += ` - Média: ${media.toFixed(2)}%<br/>`;
            })

            return tip;
          }
        },

        dataset: [
          {
            source: data
          }
        ],

        visualMap: {
          type: 'continuous',
          orient: 'vertical',
          right: 10,
          top: 'center',
          min: 0,
          max: 100,
          dimension: 'media',
          text: ['100%', '0%'],
          formatter: v => `${Math.round(v)}%`,
          textStyle: { color: '#191C1C', fontWeight: 'bold' },
          inRange: { color: colorRange },
          calculable: false,
          itemHeight: 180,
          itemWidth: 12
        },

        series: [
          {
            type: 'funnel',
            left: '10%',
            top: 20,
            bottom: 20,
            width: '70%',
            sort: 'descending',
            gap: 4,
            minSize: '10%',
            maxSize: '100%',
            encode: {
              itemName: 'name',
              value: 'value'
            },
            label: {
              show: true,
              formatter: ({ data }) =>
                `${data.name}\n${data.value.toLocaleString('pt-BR')} insc.`
            },
            itemStyle: {
              borderColor: '#FFFFFF',
              borderWidth: 1
            }
          }
        ]
      };

      this.barChart.setOption(option);
    },

    calculateAxisParams(maxValue) {
        if (maxValue === 0) {
            return { max: 10, interval: 1 };
        }
        const niceSteps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000, 2000000, 5000000, 10000000];
        let bestStep = 1;
        
        for(let i = 0; i < niceSteps.length; i++){
            if(niceSteps[i] * 10 >= maxValue){
                bestStep = niceSteps[i];
                break;
            }
        }
        const newMax = Math.ceil(maxValue / (bestStep * 10)) * (bestStep * 10);
        const newInterval = newMax / 10;
    
        return { max: newMax, interval: newInterval };
    },

    async handleSearchSubmit(searchValue) {
      this.isLoading = true;
      const url = 'insights/getReportStep';
      const data = { search: searchValue };
      try {
        const newData = await PostWithJson(url, JSON.stringify(data));

        if (newData) {
          this.internalData = newData;
          this.page = 1;
          if (!this.originalAvailableIds) {
            this.originalAvailableIds = calculateAvailableIds(newData);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados com pesquisa:', error);
      } finally {
        this.isLoading = false;
      }
    },
  }
}
</script>

<style>
.header-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  gap: 8px;
  align-self: stretch;
}
.title-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}
.subtitle-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}
.text-page {
  color: var(--Text-Neutral-Secondary, #2E3131);
  font-family: var(--Typescale-Body-Medium-Font-family, Roboto);
  font-size: var(--Typescale-Body-Medium-Font-size, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--Typescale-Body-Medium-Line-height, 20px);
  letter-spacing: var(--Typescale-Body-Medium-Tracking, 0.25px);
  margin-bottom: 52px;
}
.configuracoes-subtitle {
  color: #191C1C;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 23.4px;
  margin-top: 52px;
}
</style>